// src/services/api.js
import axios from "axios";
import { API_BASE_URL } from "../utils/constants";

// Get token from localStorage — consumer frontend uses venue_app_token,
// agent dashboard uses agentToken. Check both for compatibility.
const getToken = () => {
  return localStorage.getItem("venue_app_token") ||
         localStorage.getItem("token") ||
         localStorage.getItem("agentToken") ||
         "";
};

// Default headers including authorization and region/language headers
// ✅ FIXED: Only add Authorization header if token exists and is non-empty
const getHeaders = () => {
  const token = getToken();
  const headers = {
    "Content-Type": "application/json",
    "x-region": "th",
    "accept-language": "th",
  };
  
  // Only add Authorization header if we have a real token
  if (token && token.trim() !== '') {
    headers.Authorization = `Bearer ${token}`;
  }
  
  return headers;
};

// Create axios instance with baseUrl and default headers
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: getHeaders(),
  timeout: 60000, // Default 60 second timeout
});

// Interceptor to add token to all requests (ensures latest token used)
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    // ✅ FIXED: Only set Authorization header if token exists and is non-empty
    if (token && token.trim() !== '') {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // Remove Authorization header if no valid token
      delete config.headers.Authorization;
    }

    // Add locale header so backend can serve locale-aware responses
    const locale = localStorage.getItem('hl_locale') || 'en';
    config.headers['x-locale'] = locale;

    // ✅ Debug logging for onboard-from-google endpoint
    if (config.url?.includes('onboard-from-google')) {
      console.log('🔍 Axios Interceptor - Request Details:', {
        url: config.url,
        method: config.method,
        data: config.data,
        dataType: typeof config.data,
        dataKeys: config.data ? Object.keys(config.data) : [],
        googlePlaceId: config.data?.googlePlaceId,
        name: config.data?.name,
        headers: config.headers
      });
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor to handle 401 Unauthorized responses (token expired or invalid)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // ✅ Enhanced error logging
    if (error.config?.url?.includes('onboard-from-google')) {
      console.error('❌ Axios Interceptor - Response Error:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        requestData: error.config?.data,
        url: error.config?.url
      });
    }

    if (error.response?.status === 401) {
      // Only redirect for agent-dashboard auth failures, NOT for optional
      // checks like /i18n/me (translator mode) which legitimately return 401
      // for regular consumers.
      const url = error.config?.url || '';
      const isOptionalCheck = url.includes('/i18n/me');
      if (!isOptionalCheck) {
        localStorage.removeItem("agentToken");
        localStorage.removeItem("agentUser");
        window.location.href = "/";
      }
    }
    return Promise.reject(error);
  }
);

// ✅ Retry helper function for network failures
const retryRequest = async (requestFn, maxRetries = 2, delayMs = 2000) => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      const isLastAttempt = attempt === maxRetries;
      const isNetworkError =
        error.code === "ERR_NETWORK" || error.message === "Network Error";
      const isTimeout = error.code === "ECONNABORTED";

      if (isLastAttempt || (!isNetworkError && !isTimeout)) {
        throw error;
      }

      console.log(
        `🔄 Retry attempt ${attempt + 1}/${maxRetries} after ${delayMs}ms...`
      );
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      delayMs *= 2; // Exponential backoff
    }
  }
};

// ==== Admin APIs ====
export const adminAPI = {
  getVenuesForMap: () => api.get("/admin/venues/map"),
  assignVenuesToAgent: (data) => api.post("/admin/venues/assign", data),
  unassignVenue: (tempVenueId) =>
    api.delete(`/admin/venues/${tempVenueId}/assignment`),
  getAgents: () => api.get("/admin/agents"),
  getAssignmentStats: () => api.get("/admin/assignments/stats"),
  deleteVenue: (tempVenueId) => api.delete(`/admin/venues/${tempVenueId}`),
};

// ==== Agent APIs ====
export const agentAPI = {
  // AUTH APIs
  sendEmailOTP: (data) =>
    axios.post(`${API_BASE_URL}/auth/send-email-otp`, data),
  verifyEmailOTP: (data) =>
    axios.post(`${API_BASE_URL}/auth/verify-email-otp`, data),
  sendPhoneOTP: (data) => axios.post(`${API_BASE_URL}/auth/send-otp`, data),
  verifyPhoneOTP: (data) => axios.post(`${API_BASE_URL}/auth/verify-otp`, data),

  // ✅ GET VENUE BY TEMP ID
  getVenueByTempId: (tempVenueId) => 
    api.get(`/agent/venues/${tempVenueId}`),

  // VENUE OPS
  quickAddVenue: (data) => api.post("/agent/venues/quick-add", data),
  getAgentVenues: (params) => api.get("/agent/venues", { params }),
  getRegionalVenues: () => api.get("/agent/venues/regional"),

  // ✅ Onboard from Google with comprehensive debugging
  onboardFromGoogle: (placeData) => {
    console.log("📤 API: Sending Google place data to backend:", placeData.name);
    console.log("📦 API: Full request body structure:", {
      googlePlaceId: placeData.googlePlaceId,
      name: placeData.name,
      latitude: placeData.latitude,
      longitude: placeData.longitude,
      allKeys: Object.keys(placeData),
      dataType: typeof placeData,
      isObject: placeData instanceof Object,
      stringified: JSON.stringify(placeData, null, 2).substring(0, 500) + '...'
    });

    // ✅ Validate data before sending
    if (!placeData.googlePlaceId || !placeData.name) {
      console.error('❌ API: Missing required fields before sending!', {
        googlePlaceId: placeData.googlePlaceId,
        name: placeData.name
      });
    }

    // ✅ Send placeData directly (not nested)
    return api.post("/agent/venues/onboard-from-google", placeData)
      .then((response) => {
        console.log("📥 API: Received response from backend:", response.data);

        // ✅ Log auto-linking status
        if (response.data.autoLinked && response.data.autoCreated) {
          console.log("✅ API: Venue auto-linked successfully");
          console.log("  - Temp ID:", response.data.data.tempVenueId);
          console.log("  - CRM ID:", response.data.data.crmId);
          console.log("  - Venue ID:", response.data.data.venueId);
        } else if (response.data.autoLinked === false) {
          console.warn("⚠️ API: Auto-link failed:", response.data.linkError);
        }

        return response;
      })
      .catch((error) => {
        console.error("❌ API: Request failed:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          responseData: error.response?.data,
          message: error.message
        });
        throw error;
      });
  },

  updateVenueStatus: (tempVenueId, data) =>
    api.put(`/agent/venues/${tempVenueId}/status`, data),

  // ✅ Keep for manual linking fallback
  linkToCRM: (tempVenueId, data) =>
    api.put(`/agent/venues/${tempVenueId}/link-crm`, data),

  finalizeOnboarding: (tempVenueId) =>
    api.post(`/agent/venues/${tempVenueId}/finalize`),

  // ZONE OPS
  createZone: (venueId, data) =>
    api.post(`/agent/venues/${venueId}/zones`, data),

  // ✅ NEW: Update zone
  updateZone: (venueId, zoneId, data) =>
    api.put(`/agent/venues/${venueId}/zones/${zoneId}`, data),

  getZones: (venueId) => api.get(`/agent/venues/${venueId}/zones`),

  deleteZone: (venueId, zoneId) =>
    api.delete(`/agent/venues/${venueId}/zones/${zoneId}`),

  // ✅ ZONE PHOTO UPLOAD
  uploadZonePhoto: (formData, onUploadProgress) =>
    retryRequest(
      () => {
        const config = {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 300000, // 5 minutes for large files
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        };

        if (typeof onUploadProgress === 'function') {
          config.onUploadProgress = onUploadProgress;
        }

        return api.post('/agent/zones/upload-photo', formData, config);
      },
      2, // 2 retries
      3000 // 3 second initial delay
    ),

  // QR OPS
  attachMainQR: (venueId, data) =>
    api.post(`/agent/venues/${venueId}/qr/main`, data),
  getMainQR: (venueId) => api.get(`/agent/venues/${venueId}/qr/main`),
  linkTableQR: (venueId, data) =>
    api.post(`/agent/venues/${venueId}/qr/table`, data),
  getTableQRs: (venueId) => api.get(`/agent/venues/${venueId}/qr/table`),
  deleteTableQR: (venueId, bindingId) =>
    api.delete(`/agent/venues/${venueId}/qr/table/${bindingId}`),
  generateTestToken: (bindingId) =>
    api.post(`/agent/qr/${bindingId}/test-token`),

  // PHOTO OPS (Legacy)
  uploadPhotos: (venueId, formData) =>
    api.post(`/agent/venues/${venueId}/photos`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  getPhotos: (venueId, type) =>
    api.get(`/agent/venues/${venueId}/photos`, { params: { type } }),
  deletePhoto: (venueId, assetId) =>
    api.delete(`/agent/venues/${venueId}/photos/${assetId}`),

  // ✅ MEDIA OPS (New - S3 Direct Upload with Retry Logic)
  uploadVenueMedia: (tempVenueId, formData, onUploadProgress) =>
    retryRequest(
      () => {
        const config = {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 300000, // 5 minutes for large files
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        };

        if (typeof onUploadProgress === 'function') {
          config.onUploadProgress = onUploadProgress;
        }

        return api.post(`/agent/venues/${tempVenueId}/media`, formData, config);
      },
      2, // 2 retries
      3000 // 3 second initial delay
    ),

  getVenueMedia: (tempVenueId) => api.get(`/agent/venues/${tempVenueId}/media`),

  getMediaStats: (tempVenueId) =>
    api.get(`/agent/venues/${tempVenueId}/media/stats`),

  deleteVenueMedia: (tempVenueId, mediaId) =>
    api.delete(`/agent/venues/${tempVenueId}/media/${mediaId}`),

  // WIFI OPS (Legacy)
  runWiFiTest: (venueId, data) =>
    api.post(`/agent/venues/${venueId}/wifi-test`, data),
  getWiFiTests: (venueId) => api.get(`/agent/venues/${venueId}/wifi-tests`),

  // VISIT OPS
  logVisit: (data) => api.post("/agent/visits", data),
  getVisits: (venueId) => api.get(`/agent/visits/${venueId}`),

  // GPS OPS
  updateVenueGPS: (tempVenueId, gpsData) =>
    api.put(`/agent/venues/${tempVenueId}/gps`, gpsData),

  // ✅ GEOFENCING APIs
  updateVenueGeofence: (tempVenueId, geofenceData) =>
    api.put(`/agent/venues/${tempVenueId}/geofence`, geofenceData),

  getVenueGeofence: (tempVenueId) =>
    api.get(`/agent/venues/${tempVenueId}/geofence`),

  deleteVenueGeofence: (tempVenueId) =>
    api.delete(`/agent/venues/${tempVenueId}/geofence`),

  // ✅ PAYMENT TYPES APIs (Legacy)
  updatePaymentTypes: (tempVenueId, data) =>
    api.put(`/agent/venues/${tempVenueId}/payment-types`, data),

  getPaymentTypes: (tempVenueId) =>
    api.get(`/agent/venues/${tempVenueId}/payment-types`),

  // ✅ PAYMENT METHODS APIs (New)
  updatePaymentMethods: (tempVenueId, data) =>
    api.put(`/agent/venues/${tempVenueId}/payment-methods`, data),

  getPaymentMethods: (tempVenueId) =>
    api.get(`/agent/venues/${tempVenueId}/payment-methods`),

  // ✅ CARD MACHINE APIs
  addCardMachine: (venueId, data) =>
    api.post(`/agent/venues/${venueId}/card-machines`, data),

  getCardMachines: (venueId) =>
    api.get(`/agent/venues/${venueId}/card-machines`),

  deleteCardMachine: (venueId, machineId) =>
    api.delete(`/agent/venues/${venueId}/card-machines/${machineId}`),

  // ✅ UPI/QR PAYMENT APIs
  addUpiQrPayment: (venueId, data) =>
    api.post(`/agent/venues/${venueId}/upi-qr-payments`, data),

  getUpiQrPayments: (venueId) =>
    api.get(`/agent/venues/${venueId}/upi-qr-payments`),

  deleteUpiQrPayment: (venueId, qrId) =>
    api.delete(`/agent/venues/${venueId}/upi-qr-payments/${qrId}`),

  // ✅ QR CODE PARSER
  parseQrCode: (data) =>
    api.post('/agent/parse-qr-code', data),

  updateVenueInfo: (tempVenueId, venueData) =>
    api.put(`/agent/venues/${tempVenueId}/info`, venueData),

  // ✅ CATEGORY & TYPE APIs
  updateVenueCategoryType: (tempVenueId, data) =>
    api.put(`/agent/venues/${tempVenueId}/category-type`, data),

  getVenueCategoryType: (tempVenueId) =>
    api.get(`/agent/venues/${tempVenueId}/category-type`),

  // ✅ VITALS OPERATIONS
  getVenueVitals: (tempVenueId) =>
    api.get(`/agent/venues/${tempVenueId}/vitals`),

  createVenueVitals: (tempVenueId, vitalsData) =>
    api.post(`/agent/venues/${tempVenueId}/vitals`, vitalsData),

  updateVenueVitals: (tempVenueId, vitalsData) =>
    api.put(`/agent/venues/${tempVenueId}/vitals`, vitalsData),

  // ASSIGNMENTS
  getMyAssignments: (params) => api.get("/agent/my-assignments", { params }),
  markVenueVisited: (tempVenueId, data) =>
    api.put(`/agent/venues/${tempVenueId}/visit`, data),
  softOnboardVenue: (tempVenueId, data) =>
    api.post(`/agent/venues/${tempVenueId}/soft-onboard`, data),
  declineVenue: (tempVenueId, data) =>
    api.post(`/agent/venues/${tempVenueId}/decline`, data),
  captureLeadVenue: (tempVenueId, data) =>
    api.post(`/agent/venues/${tempVenueId}/capture-lead`, data),
  getMyStats: () => api.get("/agent/my-stats"),

  // ✅ NOTES OPERATIONS
  addVenueNote: (tempVenueId, data) =>
    api.post(`/agent/venues/${tempVenueId}/notes`, data),

  getVenueNotes: (tempVenueId, noteType = null) =>
    api.get(`/agent/venues/${tempVenueId}/notes`, {
      params: noteType ? { noteType } : {},
    }),

  updateVenueNote: (tempVenueId, noteId, data) =>
    api.put(`/agent/venues/${tempVenueId}/notes/${noteId}`, data),

  deleteVenueNote: (tempVenueId, noteId) =>
    api.delete(`/agent/venues/${tempVenueId}/notes/${noteId}`),

  // ✅ EVENTS OPERATIONS
  getVenueEvents: (tempVenueId) =>
    api.get(`/agent/venues/${tempVenueId}/events`),

  addVenueEvent: (tempVenueId, eventData) =>
    api.post(`/agent/venues/${tempVenueId}/events`, eventData),

  updateVenueEvent: (tempVenueId, eventId, eventData) =>
    api.put(`/agent/venues/${tempVenueId}/events/${eventId}`, eventData),

  deleteVenueEvent: (tempVenueId, eventId) =>
    api.delete(`/agent/venues/${tempVenueId}/events/${eventId}`),

  // ✅ NEW: EVENT PHOTO UPLOAD (Same pattern as Zone Photo)
  uploadEventPhoto: (photoFile, onUploadProgress) =>
    retryRequest(
      () => {
        const formData = new FormData();
        formData.append('eventPhoto', photoFile);

        const config = {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 300000, // 5 minutes for large files
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        };

        if (typeof onUploadProgress === 'function') {
          config.onUploadProgress = onUploadProgress;
        }

        return api.post('/agent/events/upload-photo', formData, config);
      },
      2, // 2 retries
      3000 // 3 second initial delay
    ),
};

// ==== ✅ WiFi Speed Test APIs ====
export const wifiSpeedAPI = {
  submitSpeedTest: (data) => api.post("/wifi-speed/test", data),
  getVenueSpeedTests: (venueId, params = {}) =>
    axios.get(`${API_BASE_URL}/wifi-speed/venue/${venueId}`, { params }),
  getMySpeedTests: (params = {}) => api.get("/wifi-speed/my-tests", { params }),
  deleteSpeedTest: (testId) => api.delete(`/wifi-speed/test/${testId}`),
  getVenueWiFiStats: (venueId) =>
    axios.get(`${API_BASE_URL}/wifi-speed/venue/${venueId}/stats`),
  getVenueWifiSSIDs: (venueId) =>
    axios.get(`${API_BASE_URL}/wifi-speed/venue/${venueId}/ssids`, { params: { consumer: 'true' } }),
};

// ==== User APIs (Public) ====
export const userAPI = {
  getVenueDetails: (venueId) => axios.get(`${API_BASE_URL}/venues/${venueId}`),
  submitSpeedTestPublic: (data) =>
    axios.post(`${API_BASE_URL}/wifi-speed/test`, data, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
    }),
  getVenueSpeedTestsPublic: (venueId) =>
    axios.get(`${API_BASE_URL}/wifi-speed/venue/${venueId}`),
};

// ==== Curator API (EV-588 — curators add places) ====
// Uses the auth'd `api` instance (Bearer token interceptor). All routes are
// curator-gated server-side (auth.users.is_curator or ADMIN/MANAGER).
export const curatorAPI = {
  // Is this place already in HonestLee?
  searchVenues: (q) => api.get('/curator/venues/search', { params: { q } }),
  // Google Places, GPS/DN-biased, NEW-flagged + distance-sorted
  searchPlaces: (q, lat, lng) => api.get('/curator/places/search', { params: { q, lat, lng } }),
  // "Places near me" discovery — Google Nearby around the curator's GPS
  nearbyPlaces: (lat, lng, radius) => api.get('/curator/places/nearby', { params: { lat, lng, radius } }),
  // Full Google Place Details (hero photo, rating, address, hours…) for the review write-up
  placeDetails: (placeId) => api.get(`/curator/places/${placeId}/details`),
  // Prospect + audit. Pass { place_id, name, lat, lng, region, note } OR { existing_venue_id, note }
  addPlace: (body) => api.post('/curator/places/add', body),
  // The curator's own curated places
  myPlaces: () => api.get('/curator/my-places'),
  // Category assignment (steal-from-field-agent model)
  taxonomy: () => api.get('/curator/taxonomy'),
  getVenueCategory: (venueId) => api.get(`/curator/venues/${venueId}/category`),
  setVenueCategory: (venueId, typeId) => api.put(`/curator/venues/${venueId}/category`, { type_id: typeId }),
  // ADMIN — list all curators + view a curator's places
  listCurators: () => api.get('/curator/curators'),
  curatorPlaces: (userId) => api.get(`/curator/curators/${userId}/places`),
};

// ==== Payments API (consumer) ====
export const paymentAPI = {
  // EV-548 — resolve a scanned VietQR (bank_bin + account_number) → HonestLee venue
  resolveQr: (body) => api.post('/payments/resolve-qr', body),
};

// ==== Venues API (for SpeedTestPage) ====
export const venues = {
  getById: (venueId) => axios.get(`${API_BASE_URL}/venues/${venueId}`),
  getAll: (params) => axios.get(`${API_BASE_URL}/venues`, { params }),
};

// ==== i18n Content API ====
export const i18nAPI = {
  // Get published translations for an entity in a locale
  // Returns { translations: { name: "...", description: "..." } } or {} if none found
  getContent: (entityType, entityId, locale, fields) => {
    const params = fields ? { fields: fields.join(',') } : {};
    return api.get(`/i18n/content/${entityType}/${entityId}/${locale}`, { params });
  },

  // Get active locales
  getLocales: () => api.get('/i18n/locales'),

  // Submit a translation correction
  submitCorrection: (data) => api.post('/i18n/correction', data),

  // Get current user's i18n profile (role + preferred_locale)
  // Used to detect TRANSLATOR role for the in-app correction flow
  getMe: () => api.get('/i18n/me'),

  // UI string overrides — fetch published overrides for a locale
  getUIStrings: (locale) => api.get(`/i18n/ui-strings/${locale}`),

  // Submit a UI string correction (TRANSLATOR+)
  submitUICorrection: (data) => api.post('/i18n/ui-strings/correct', data),

  // Admin: sync JSON files to DB
  syncUIStrings: () => api.post('/i18n/admin/ui-strings/sync'),

  // Admin: list UI strings with filters
  listUIStrings: (params) => api.get('/i18n/admin/ui-strings', { params }),

  // Admin: UI string stats per module/locale
  getUIStringStats: () => api.get('/i18n/admin/ui-strings/stats'),

  // Admin: verify a UI string
  verifyUIString: (id) => api.post(`/i18n/admin/ui-strings/${id}/verify`),
};

export default api;
